import {
  ActivityWebCalendarSettings,
  useGetDatabaseRulesSuspense,
} from "@/orval/gen"
import { CancelButton, SaveButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { InfiniteUserMultiSelect } from "@components/InfiniteUserSelect"
import { ProductionMultiSelect } from "@components/multi-select/ProductionMultiSelect"
// import { ActivityStatusTreeSelect } from "@components/tree-select/ActivityStatusTreeSelect"
import { ActivityTypeTreeSelect } from "@components/tree-select/ActivityTypeTreeSelect"
import { CollectiveTreeSelect } from "@components/tree-select/CollectiveTreeSelect"
import { GenreTreeSelect } from "@components/tree-select/GenreTreeSelect"
import { LocationTreeSelect } from "@components/tree-select/LocationTreeSelect"
// import { OwnerGroupTreeSelect } from "@components/tree-select/OwnerGroupTreeSelect"
import { Stack, TextInput, Textarea, Checkbox } from "@mantine/core"
import { DatesRangeValue } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { DateRangePickerInputWithSeasonPresets } from "@components/date-picker/DateRangePickerInputWithSeasonPresets"

export interface ActivityCalendarFormValues {
  name: string
  description?: string | null
  settings: ActivityWebCalendarSettings
  isPublic: boolean
  visibleForUserIds?: number[]
}

interface ActivityCalendarFormProps {
  onClose: () => void
  handleSubmit: (values: ActivityCalendarFormValues) => Promise<void>
  initialValues: ActivityCalendarFormValues
}

export function ActivityCalendarForm({
  onClose,
  handleSubmit,
  initialValues,
}: ActivityCalendarFormProps) {
  React.useEffect(() => {
    form.setValues(initialValues)
  }, [initialValues])

  const { t } = useTranslation("app_server")

  const form = useForm<ActivityCalendarFormValues>({
    mode: "uncontrolled",
    initialValues,
  })

  const { data: databaseRoles } = useGetDatabaseRulesSuspense()
  const canCreatePublicWebCalendars = databaseRoles.includes(
    "CreatePublicWebCalendarProfiles"
  )

  const dateRangeValue: DatesRangeValue<string> = React.useMemo(
    () => [
      form.getValues().settings.start ?? "",
      form.getValues().settings.end ?? "",
    ],
    [form]
  )
  const handleDateRangeChange = React.useCallback(
    (value: DatesRangeValue<string>) => {
      form.setFieldValue("settings.start", value[0] || undefined)
      form.setFieldValue("settings.end", value[1] || undefined)
    },
    [form]
  )

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          key={form.key("name")}
          required
          label={t("Name")}
          {...form.getInputProps("name")}
        />
        <DateRangePickerInputWithSeasonPresets
          label={t("TimePeriod")}
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          required
        />
        <Textarea
          key={form.key("description")}
          label={t("Description")}
          {...form.getInputProps("description")}
        />
        <ProductionMultiSelect
          period={{
            start: form.getValues().settings?.start ?? undefined,
            end: form.getValues().settings?.end ?? undefined,
          }}
          value={form.getValues().settings?.productionIds}
          onChange={(value) =>
            form.setFieldValue("settings.productionIds", value)
          }
        />
        <LocationTreeSelect
          value={form.getValues().settings?.locationNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.locationNodeIds", value)
          }
        />
        <ActivityTypeTreeSelect
          value={form.getValues().settings?.activityTypeNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.activityTypeNodeIds", value)
          }
        />
        <GenreTreeSelect
          value={form.getValues().settings?.genreNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.genreNodeIds", value)
          }
        />
        <CollectiveTreeSelect
          value={form.getValues().settings?.collectiveNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.collectiveNodeIds", value)
          }
        />
        {/* <ActivityStatusTreeSelect
          value={form.getValues().settings?.activityStatusNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.activityStatusNodeIds", value)
          }
        />
        <OwnerGroupTreeSelect
          value={form.getValues().settings?.ownerGroupNodeIds}
          onChange={(value) =>
            form.setFieldValue("settings.ownerGroupNodeIds", value)
          }
        /> */}
        {canCreatePublicWebCalendars && (
          <>
            <Checkbox
              key={form.key("isPublic")}
              label={t("Public")}
              {...form.getInputProps("isPublic", { type: "checkbox" })}
            />
            {form.getValues().isPublic && (
              <InfiniteUserMultiSelect
                value={form.getValues().visibleForUserIds?.map(String) ?? []}
                onSelectionChange={(value) =>
                  form.setFieldValue("visibleForUserIds", value.map(Number))
                }
                filterOptions={{ userTypeFilter: "Groups" }}
              />
            )}
          </>
        )}
        <FormButtonGroup>
          <CancelButton onClick={onClose} />
          <SaveButton />
        </FormButtonGroup>
      </Stack>
    </form>
  )
}
